import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const sheetOverlayVariants = cva(
  "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  {
    variants: {
      intensity: {
        default: "bg-black/40",
        heavy: "bg-black/60",
        none: "bg-transparent",
      },
    },
    defaultVariants: {
      intensity: "default",
    },
  },
);

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
    intensity?: "default" | "heavy" | "none";
  }
>(({ className, intensity = "default", ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(sheetOverlayVariants({ intensity }), className)}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out " +
    "data-[state=open]:animate-in data-[state=closed]:animate-out " +
    "data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
      size: {
        sm: "max-h-[50vh]",
        md: "max-h-[70vh]",
        lg: "max-h-[85vh]",
        xl: "max-h-[95vh]",
        full: "h-full",
      },
      mobileBehavior: {
        bottom: "bottom-0 left-0 right-0 top-auto h-auto max-h-[90vh] rounded-t-[10px] sm:rounded-t-2xl",
        full: "inset-0 rounded-none",
      },
    },
    compoundVariants: [
      {
        side: "bottom",
        mobileBehavior: "bottom",
        className: "sm:max-w-full sm:max-h-[85vh]",
      },
      {
        side: "right",
        mobileBehavior: "bottom",
        className: "sm:inset-y-0 sm:right-0 sm:h-full sm:w-3/4 sm:border-l sm:max-w-sm sm:rounded-none",
      },
    ],
    defaultVariants: {
      side: "right",
      size: "lg",
      mobileBehavior: "bottom",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", size, mobileBehavior, className, children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay intensity={mobileBehavior === "full" ? "heavy" : "default"} />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        sheetVariants({ side, size, mobileBehavior }),
        "slateCardClass",
        className,
      )}
      {...props}
    >
      {children}
      <SheetPrimitive.Close className={cn(
        "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity",
        "data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
        "hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        "disabled:pointer-events-none",
        "actionPillClass",
      )}>
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      "pb-4 border-b",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      "pt-4 border-t",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  type SheetContentProps,
  sheetVariants,
};